import React, { useState, useEffect, useRef } from "react";
import { baseUrl } from '../../shared';
import SignaturePad from "react-signature-canvas";
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import '../../pages/App.css';

function RepairPhotoProxy({ repairId, field }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    fetch(baseUrl + 'repair/photo/' + repairId + '/' + field, {
      headers: {
        username: localStorage.getItem('username'),
        bToken: localStorage.getItem('bToken'),
      },
    })
      .then(r => r.blob())
      .then(blob => setSrc(URL.createObjectURL(blob)));
  }, [repairId, field]);

  return src ? <img src={src} alt={field} width="100%" /> : <span>Loading photo...</span>;
}

function RepairForm({ assetId, repairId, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    assetId: assetId,
    repairCost: "0",
    pickupSignature: "",
    pickupApprovedBy: "",
    returnSignature: "",
    returnApprovedBy: "",
    status: "Arrived",
    notes: [],
    sendEmail: false,
  });

  const [pickupPhotoFile, setPickupPhotoFile] = useState(null);
  const [returnPhotoFile, setReturnPhotoFile] = useState(null);

  const pickupSigRef = useRef();
  const returnSigRef = useRef();
  var [reloadRepair, setReloadRepair] = useState(true);
  var [formSubmitted, setFormSubmitted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [repairCost, setRepairCost] = useState(null);

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (field === 'pickupPhoto') setPickupPhotoFile(file);
    if (field === 'returnPhoto') setReturnPhotoFile(file);
  };

  const handleClearSig = (ref, field) => {
    ref.current.clear();
    setForm({ ...form, [field]: null });
  };

  const handleSaveSig = (ref, field) => {
    setForm({ ...form, [field]: ref.current.toDataURL() });
  };

  const [newNote, setNewNote] = useState("");

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange({ target: { files: [file] } }, "pickupPhoto");
  };

  useEffect(() => {
    const repairCosting = baseUrl + 'repair/asset-costing/' + assetId;
    fetch(repairCosting, {
        method: 'GET',
        headers: {
            'username': localStorage?.getItem('username'),
            'bToken': localStorage?.getItem('bToken')
        }})
      .then((res) => res.json())
      .then((data) => setRepairCost(data));
  }, [assetId]);

  useEffect(() => {
    if (!repairId) return;
    if (!reloadRepair) return;
    const fetchData = async () => {
      try {
        const headers = {
          username: localStorage?.getItem("username"),
          bToken: localStorage?.getItem("bToken"),
        };

        const [repairRes, notesRes] = await Promise.all([
          fetch(baseUrl + "repair/asset-repair/" + repairId, { headers }),
          fetch(baseUrl + "repair/asset-repair/" + repairId + "/notes", { headers }),
        ]);

        const repair = await repairRes.json();
        const notes = await notesRes.json();

        setForm((prev) => ({
          ...prev,
          ...repair,
          notes: notes || prev.notes || [],
          sendEmail: prev.sendEmail ?? true,
        }));

        console.log("Form loaded:", repair, notes);
        setReloadRepair(false);
      } catch (err) {
        console.error("Failed to load repair:", err);
      }
    };

    fetchData();
  }, [repairId, reloadRepair]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddNote = (newNote) => {
    if (newNote.trim()) {
      setForm((prev) => ({
        ...prev,
        notes: [...prev.notes, newNote.trim()],
      }));
      setNewNote("");
      console.log('New note: ', form)
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    const mergedNotes = newNote.trim()
      ? [...form.notes, { text: newNote.trim() }]
      : form.notes;

    const endpoint = repairId
      ? baseUrl + "repair/update-asset-repair/" + repairId
      : baseUrl + "repair/create-asset-repair";

    const payload = {
      ...form,
      notes: mergedNotes
        .filter((n) => n && (n.text || n.noteText))
        .map((n) => ({
          noteId: n.noteId,
          noteText: n.text || n.noteText,
        })),
    };

    setNewNote("");

    const saveRes = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        username: localStorage.getItem("username"),
        bToken: localStorage.getItem("bToken"),
      },
      body: JSON.stringify(payload),
    }).then(r => r.json());

    const savedRepairId = repairId || saveRes.repair?.repairId;

    const photoUploads = [
      { file: pickupPhotoFile, field: 'pickupPhoto' },
      { file: returnPhotoFile, field: 'returnPhoto' },
    ].filter(p => p.file && savedRepairId);

    for (const { file, field } of photoUploads) {
      const formData = new FormData();
      formData.append('photo', file);
      await fetch(baseUrl + 'repair/photo/' + savedRepairId + '/' + field, {
        method: 'POST',
        headers: {
          username: localStorage.getItem('username'),
          bToken: localStorage.getItem('bToken'),
        },
        body: formData,
      });
    }

    setPickupPhotoFile(null);
    setReturnPhotoFile(null);

    alert("Repair saved!");
    setReloadRepair(true);

    if (onSuccess) {
      onSuccess();
    }
  };

  const deleteNote = async (noteId) => {
    const endpoint = baseUrl + "repair/notes/delete/";
    await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        username: localStorage.getItem("username"),
        bToken: localStorage.getItem("bToken"),
        noteId: noteId,
      },
    });
    alert("Note deleted!");
    setReloadRepair(true);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", maxHeight: "82vh", width: "100%", margin: 0 }}>
      {/* Pinned header */}
      <Box sx={{ flexShrink: 0, px: 3, py: 1.5, borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text)' }}>{repairId ? "Update Repair/C&D" : "Create Repair/C&D"}</Typography>
      </Box>
      {/* Scrollable middle */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2, display: 'grid', gap: '1rem' }}>

  <Box
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      gap: 0.5,
      backgroundColor: 'grey.100',
      border: '1px solid',
      borderColor: 'grey.300',
      borderRadius: 2,
      px: 2,
      py: 1.5,
      minWidth: 180,
    }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      Current Total Cost of Asset Repairs
    </Typography>
    <Typography variant="body2" fontWeight={600} color="text.primary">
      {new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
      }).format(repairCost?.repairCostTotal)}
    </Typography>
  </Box>

      <TextField
        fullWidth
        name="assetId"
        InputProps={{
          readOnly: true,
        }}
        label="Asset Id"
        placeholder="Asset ID"
        value={form.assetId ?? ''}
        onChange={handleChange}
        InputLabelProps={{
          shrink: Boolean(form.assetId),
        }}
        required
      />
      <Select style={{ padding: 0, margin: 0, maxWidth: "100%" }} name="status" value={form.status ?? "Collected"} onChange={handleChange}>
        <MenuItem value="Arrived">Arrived</MenuItem>
        <MenuItem value="Collected">Collected</MenuItem>
        <MenuItem value="InProgress">In Progress</MenuItem>
        <MenuItem value="AwaitingApproval">Awaiting Approval</MenuItem>
        <MenuItem value="AwaitingParts">Awaiting Parts</MenuItem>
        <MenuItem value="Complete">Complete</MenuItem>
        <MenuItem value="Returned">Returned</MenuItem>
      </Select>

      <TextField
        fullWidth
        required
        label="Pickup Approved By"
        name="pickupApprovedBy"
        placeholder="Pickup Approved By"
        value={form.pickupApprovedBy ?? ''}
        onChange={handleChange}
        InputLabelProps={{
          shrink: Boolean(form.pickupApprovedBy),
        }}
      />

      <TextField
        fullWidth
        required
        label="Job Card"
        name="jobCard"
        placeholder="Job Card"
        value={form.jobCard ?? ''}
        onChange={handleChange}
        InputLabelProps={{
          shrink: Boolean(form.jobCard),
        }}
      />

      <TextField
        fullWidth
        required
        label="Purchase Order"
        name="purchaseOrder"
        placeholder="Purchase Order"
        value={form.purchaseOrder ?? ''}
        onChange={handleChange}
        InputLabelProps={{
          shrink: Boolean(form.purchaseOrder),
        }}
      />

      <TextField
        fullWidth
        required
        label="Line Item"
        name="lineItem"
        placeholder="Line Item"
        value={form.lineItem ?? ''}
        onChange={handleChange}
        InputLabelProps={{
          shrink: Boolean(form.lineItem),
        }}
      />

  {(form.status === 'Complete' || form.status === 'Returned') && (
  <TextField
    fullWidth
    required
    label="Repair Cost (Ex GST)"
    name="repairCost"
    placeholder="0.00"
    value={form.repairCost ?? ''}
    onChange={(e) => {
      const value = e.target.value;
      if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
        handleChange(e);
      }
    }}
    InputLabelProps={{
      shrink: Boolean(form.repairCost),
    }}
    inputProps={{
      inputMode: 'decimal',
      pattern: '[0-9]*\\.?[0-9]{0,2}',
    }}
  />
)}

      <div
        className={`drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        style={{ border: isDragging ? '2px dashed #007bff' : '2px dashed #ccc', padding: '20px' }}
      >
        <label>Photo</label>
        {pickupPhotoFile ? (
          <img src={URL.createObjectURL(pickupPhotoFile)} alt="Pickup preview" width="100%" />
        ) : form.pickupPhotoUrl ? (
          <RepairPhotoProxy repairId={repairId} field="pickupPhoto" />
        ) : form.pickupPhoto ? (
          <img src={form.pickupPhoto} alt="Pickup" width="100%" />
        ) : (
          <>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, "pickupPhoto")}
              required={!repairId}
              id="photo-input"
            />
            <p>Drag and drop or click to select from gallery/files</p>
          </>
        )}
      </div>

      <label>Pickup Signature</label>
      {!form.pickupSignature ? (
        <div>
      <SignaturePad
        ref={pickupSigRef}
        canvasProps={{
        className: "sigCanvas",
        style: {
          width: "100%",
          maxWidth: "400px",
          height: "150px",
          border: "1px solid #ccc",
          borderRadius: "8px",
        },
      }}
      />
      <br/>
      <Button margin="dense" variant="contained" style={{maxWidth: '60%', height: 'auto' }} onClick={() => handleSaveSig(pickupSigRef, "pickupSignature")}>
        Save Signature
      </Button>
      <Button margin="dense" variant="contained" style={{maxWidth: '60%', height: 'auto' }} onClick={() => handleClearSig(pickupSigRef, "pickupSignature")}>
        Clear
      </Button>
      </div>):null}
      {form.pickupSignature && <img src={form.pickupSignature} alt="Pickup Sig" width="150" />}

    { form?.status === 'Returned' ? (
    <div>
      <TextField
        label="Return Approved By"
        name="returnApprovedBy"
        placeholder="Return Approved By"
        value={form.returnApprovedBy}
        onChange={handleChange}
      />
<br />
        <label>Photo</label>
<br />
      {returnPhotoFile ? (
        <img src={URL.createObjectURL(returnPhotoFile)} alt="Return preview" width="150" />
      ) : form.returnPhotoUrl ? (
        <RepairPhotoProxy repairId={repairId} field="returnPhoto" />
      ) : (
        <>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleFileChange(e, "returnPhoto")}
          />
          {form.returnPhoto && <img src={form.returnPhoto} alt="Return" width="150" />}
        </>
      )}
<br />
      <label>Return Signature</label>
<br />
      {!form.returnSignature ? (
        <div><SignaturePad
        ref={returnSigRef}
        canvasProps={{
        className: "sigCanvas",
        style: {
          width: "100%",
          maxWidth: "400px",
          height: "150px",
          border: "1px solid #ccc",
          borderRadius: "8px",
        },
      }}
      />
      <br/>
      <Button margin="dense" variant="contained" style={{maxWidth: '60%', height: 'auto' }} onClick={() => handleSaveSig(returnSigRef, "returnSignature")}>
        Save Signature
      </Button>
      <Button margin="dense" variant="contained" style={{maxWidth: '60%', height: 'auto' }} onClick={() => handleClearSig(returnSigRef, "returnSignature")}>
        Clear
      </Button>
      </div>):null}
      {form.returnSignature && <img src={form.returnSignature} alt="Return Sig" width="150" />}
       </div> ):null}
      <br />

      <label>
        <input
          type="checkbox"
          name="sendEmail"
          checked={form.sendEmail}
          onChange={handleChange}
        />{" "}
        Send Email Copy
      </label>

{/* Notes Section */}
<div>
  <h3>Notes</h3>
  <ul style={{ padding: 0, margin: 0, width: "100%" }}>
    {form.notes.map((note, idx) => {
      const text = typeof note === "string" ? note : note.noteText || note.text;
      return <li style={{'list-style-type': 'none', width: "100%"}} readOnly key={note.noteId || idx}><TextField style={{width: '85%'}} value={text} multiline/><IconButton onClick={() => {deleteNote(note.noteId)}}><DeleteIcon></DeleteIcon></IconButton></li>;
    })}
  </ul>

<TextField
  value={newNote}
  style={{ width: "85%" }}
  multiline
  placeholder="Add note"
  onChange={(e) => {
    const val = e.target.value;
    setNewNote(val);
  }}
/>
  <IconButton
    onClick={() => {
      if (newNote.trim()) {
        setForm((prev) => ({
          ...prev,
          notes: [
            ...prev.notes,
            {
              text: newNote.trim(),
            },
          ],
        }));
        setNewNote("");
      }
    }}
  >
    <AddCircleOutlineIcon />
  </IconButton>
</div>

      </Box>{/* end scrollable middle */}
      {/* Pinned footer */}
      <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
        {onCancel ? (
          <Button variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }} onClick={onCancel}>
            Cancel
          </Button>
        ) : <span />}
        <Button variant="contained" disabled={formSubmitted} type="submit">{repairId ? "Update" : "Create"}</Button>
      </Box>
    </form>
  );
}

export default RepairForm;
